import { css, useTheme } from '@emotion/react';
import { Menu } from '@mantine/core';
import type { ReactNode } from 'react';
import React, { memo, useRef, useState } from 'react';

export type MenuDropdownProps = {
  target?: ReactNode;
  targetContent?: ReactNode;
  dropdownClassName?: string;
  hover: boolean;
  ariaLabel?: string;
};

const buttonResetStyle = css`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const MenuDropdown = memo((props: MenuDropdownProps) => {
  const { target, targetContent, dropdownClassName, hover, ariaLabel } = props;
  const [opened, setOpened] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLButtonElement>(null);
  const isClosingViaEscapeRef = useRef(false);
  const theme = useTheme();
  const wrapper = theme.widgets?.MenuDropdown;

  const getMenuItems = () => {
    if (!containerRef.current) {
      return [];
    }
    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>('[data-menu-item], .mantine-Menu-item, [role="menuitem"]'),
    );
  };

  const handleTargetFocus = () => {
    if (isClosingViaEscapeRef.current) {
      isClosingViaEscapeRef.current = false;
      return;
    }
    setOpened(true);
  };

  const handleTargetKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpened(true);
      setTimeout(() => {
        const items = getMenuItems();
        if (items.length > 0) {
          items[0].focus();
        }
      }, 50);
    } else if (event.key === 'Tab' && !event.shiftKey && opened) {
      const items = getMenuItems();
      if (items.length > 0) {
        event.preventDefault();
        items[0].focus();
      }
    } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      setOpened(prev => !prev);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      isClosingViaEscapeRef.current = true;
      setOpened(false);
      setTimeout(() => {
        isClosingViaEscapeRef.current = false;
      }, 100);
    }
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      isClosingViaEscapeRef.current = true;
      setOpened(false);
      targetRef.current?.focus();
      setTimeout(() => {
        isClosingViaEscapeRef.current = false;
      }, 100);
      return;
    }

    if (event.key === 'Tab') {
      const items = getMenuItems();
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      if (event.shiftKey) {
        if (currentIndex <= 0) {
          event.preventDefault();
          targetRef.current?.focus();
        } else {
          event.preventDefault();
          items[currentIndex - 1].focus();
        }
      } else if (currentIndex >= items.length - 1) {
        setOpened(false);
      } else if (currentIndex >= 0) {
        event.preventDefault();
        items[currentIndex + 1].focus();
      }
    }
  };

  const handleClick = () => {
    if (hover) {
      setOpened(prev => !prev);
    }
  };

  const handleContainerBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocused = event.relatedTarget;
    if (nextFocused instanceof Node && containerRef.current?.contains(nextFocused)) {
      return;
    }
    setOpened(false);
  };

  return (
    <div ref={containerRef} onBlur={handleContainerBlur}>
      <Menu
        opened={opened}
        onChange={setOpened}
        classNames={{ dropdown: dropdownClassName }}
        id="mantine-menu"
        styles={wrapper}
        withArrow
        arrowSize={13}
        trigger={hover ? 'hover' : 'click'}
      >
        <Menu.Target>
          <button
            type="button"
            css={buttonResetStyle}
            ref={targetRef}
            onFocus={hover ? handleTargetFocus : undefined}
            onKeyDown={handleTargetKeyDown}
            onClick={handleClick}
            aria-label={ariaLabel}
          >
            {target}
          </button>
        </Menu.Target>
        <Menu.Dropdown onKeyDown={handleDropdownKeyDown}>{targetContent}</Menu.Dropdown>
      </Menu>
    </div>
  );
});
